import Player from '@vimeo/player';

export default class VideoVisualPlayer {
    constructor($el) {
        this.$el = $el;
        this.$toggle = this.$el.querySelector('[data-video-visual-player-toggle]')
            || this.$el.parentElement.querySelector('[data-video-visual-player-toggle]');
        this.$toggleIcon = this.$toggle.querySelector('i');

        this.startFirstVideo    = this.startFirstVideo.bind(this);
        this.playNextVideo      = this.playNextVideo.bind(this);
        this.selectNewVideo     = this.selectNewVideo.bind(this);
        this.togglePlayback     = this.togglePlayback.bind(this);
        this.addEndedListener   = this.addEndedListener.bind(this);
        this.removeEndedListener    = this.removeEndedListener.bind(this);
        this.initializeVideos   = this.initializeVideos.bind(this);

        if (this.$toggle) {
            this.$toggle.addEventListener('click', this.togglePlayback);
        }

        document.addEventListener('vimeo-consent-granted', this.initializeVideos);
        document.addEventListener('vimeo-consent-revoked', this.initializeVideos);

        this.initializeVideos();
    }

    /**
     * Rebuild the playlist when Vimeo consent adds or removes iframe sources.
     */
    initializeVideos() {
        if (this.$currentVideo) {
            this.removeEndedListener(this.$currentVideo);
            this.pauseVideo(this.$currentVideo);
        }

        this.$el.querySelectorAll('iframe').forEach(($iframe) => {
            if ($iframe.player) {
                this.handlePlayerPromise($iframe.player.unload());
                $iframe.player = null;
            }
        });

        this.$videos = Array.from(this.$el.querySelectorAll('video, iframe')).filter(($video) => (
            $video.tagName !== 'IFRAME' || $video.hasAttribute('src')
        ));

        this.$el.querySelectorAll('video, iframe').forEach(($video) => {
            $video.classList.add('hidden');
        });

        this.$videos.forEach(($video) => {
            const isVimeo = $video.tagName === 'IFRAME';
            const player = isVimeo
                ? new Player($video)
                : null;

            $video.player = player;

            if (player) {
                this.handlePlayerPromise(player.setLoop(false));
            }
        });

        this.$currentVideo = null;
        this.currentVideoIndex = 0;
        this.isPaused = false;

        if (this.$toggle) {
            this.$toggle.hidden = ! this.$videos.length;
        }

        if (this.$videos.length) {
            this.startFirstVideo();
        }
    }

    /**
     * Start up the first video
     */
    startFirstVideo() {
        this.$currentVideo = this.$videos[0];
        this.playNextVideo();
    }

    /**
     * Play the next video
     */
    playNextVideo() {
        this.addEndedListener(this.$currentVideo);

        this.$currentVideo.classList.remove('hidden');

        if ( ! this.isPaused) {
            this.playVideo(this.$currentVideo);
        }
    }

    /**
     * Toggle playback of the current video
     */
    togglePlayback() {
        if ( ! this.$currentVideo) {
            return;
        }

        this.isPaused = ! this.isPaused;

        if (this.isPaused) {
            this.$toggleIcon.classList.add('icon-play');
            this.$toggleIcon.classList.remove('icon-pause');

            this.pauseVideo(this.$currentVideo);

            return;
        }

        this.$toggleIcon.classList.add('icon-pause');
        this.$toggleIcon.classList.remove('icon-play');

        this.playVideo(this.$currentVideo);
    }

    /**
     * Select the new video after the previous one has ended and then play it
     */
    selectNewVideo() {
        this.removeEndedListener(this.$currentVideo);
        this.resetVideo(this.$currentVideo);

        this.currentVideoIndex += 1;
        let $newVideo = this.$videos[this.currentVideoIndex];

        if ( ! $newVideo) {
            this.currentVideoIndex = 0;
            $newVideo = this.$videos[this.currentVideoIndex];
        }

        this.$currentVideo.classList.add('hidden');
        this.$currentVideo = $newVideo;

        this.playNextVideo();
    }

    /**
     * Add the correct ended listener for the media type
     */
    addEndedListener($video) {
        if ($video.player) {
            $video.player.on('ended', this.selectNewVideo);

            return;
        }

        $video.addEventListener('ended', this.selectNewVideo);
    }

    /**
     * Remove the correct ended listener for the media type
     */
    removeEndedListener($video) {
        if ($video.player) {
            $video.player.off('ended', this.selectNewVideo);

            return;
        }

        $video.removeEventListener('ended', this.selectNewVideo);
    }

    /**
     * Play a native video or Vimeo iframe
     */
    playVideo($video) {
        if ($video.player) {
            this.handlePlayerPromise(
                $video.player.ready()
                    .then(() => $video.player.setMuted(true))
                    .then(() => $video.player.setAutopause(false))
                    .then(() => $video.player.play())
            );

            return;
        }

        if (typeof $video.play === 'function') {
            this.handlePlayerPromise($video.play());
        }
    }

    /**
     * Pause a native video or Vimeo iframe
     */
    pauseVideo($video) {
        if ($video.player) {
            this.handlePlayerPromise($video.player.pause());

            return;
        }

        if (typeof $video.pause === 'function') {
            $video.pause();
        }
    }

    /**
     * Reset a native video or Vimeo iframe
     */
    resetVideo($video) {
        if ($video.player) {
            this.handlePlayerPromise($video.player.setCurrentTime(0));

            return;
        }

        if ($video.tagName === 'VIDEO') {
            $video.currentTime = 0;
        }
    }

    /**
     * Avoid unhandled promise rejections when browsers block media actions
     */
    handlePlayerPromise(promise) {
        if (promise && typeof promise.catch === 'function') {
            promise.catch(() => {});
        }
    }
}
